import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-enrolment-form',
  templateUrl: './enrolment-form.component.html',
  styleUrl: './enrolment-form.component.scss',
})
export class EnrolmentFormComponent implements OnInit {
  showModal = true;
  showTermsModal = false;
  mobileForm!: FormGroup;
  enrolmentForm!: FormGroup;
  submitted = false;
  isLoading = false;
  mobileErrorMessage: string = '';
  otpCode: string = '';
  agreedToTerms = false;
  showOtpModal = false;
  showTermsError = false;
  expectedOtp: string = '';
  public stateList: any = [];
  public cityList: any = [];
  cityLists: any[][] = [];
  resendCooldown: number = 0;
  otpTimer: any;
  effectiveDate: string = '';
  otpError: string = '';

  declarationsList: string[] = [
    'I agree to maintain quality standards as required by AzeApp.',
    'I authorize AzeApp to use my workshop logo and name for marketing and listing purposes.',
    'I consent to receive notifications, updates, and promotions from AzeApp via WhatsApp, SMS, email, and phone calls.',
    'I understand that membership may be withdrawn in case of non-compliance with AzeApp guidelines.',
  ];

  equipmentList: string[] = [
    'Hydraulic Lift',
    'Scanning Tools',
    'AC Gas Machine',
    'Paint Booth',
    'Wheel Alignment',
    'Others',
  ];

  languageList: string[] = ['English', 'Tamil', 'Hindi', 'Others'];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private http: ApiService,
    public router: Router,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const yyyy = today.getFullYear();
    this.effectiveDate = `${dd}-${mm}-${yyyy}`;

    this.mobileForm = this.fb.group({
      mobileNo: ['', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]],
    });

    this.enrolmentForm = this.fb.group({
      membershipApplicationId: [],
      workshopName: ['', Validators.required],
      tradeName: [''],
      workshopType: [null, Validators.required],
      branchName: [null, Validators.required],
      gstNo: [''],
      panNo: [''],
      workshopAddress: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      locationCode: ['', Validators.required],

      ownerName: ['', Validators.required],
      mobileNo: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      alternateMobileNo: [''],
      managerName: ['', Validators.required],
      managerContact: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
      ],
      noOfMechanics: ['', Validators.required],
      workingDaysOrHours: ['', Validators.required],
      weeklyHoliday: ['', Validators.required],
      stateId: ['', Validators.required],
      state: [],
      cityId: ['', Validators.required],
      city: [''],
      isOwnedOrRented: [null, Validators.required],
      serviceBays: ['', Validators.required],
      equipments: this.fb.array([]),
      equipmentsOthers: [null],
      customerWaitingArea: [null, Validators.required],
      billingCounterWithPrinter: [null, Validators.required],
      isWms: [null, Validators.required],
      wmsName: [''],
      azeappSolutionAdoption: [null, Validators.required],

      bankName: [''],
      accountHolderName: [''],
      accountNumber: [''],
      ifscCode: [''],
      upiId: [''],

      minimumMonthlySource: ['', Validators.required],
      language: this.fb.array([]),
      languageOthers: [],
      whatsappNumber: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
      ],
      declarations: this.fb.array([]),

      additionalWorkshops: this.fb.array([]), // nested workshops
    });

    this.enrolmentForm.get('isWms')?.valueChanges.subscribe((value) => {
      if (value === '2') {
        this.enrolmentForm.get('wmsName')?.reset();
      }
    });

    this.http.get(`retailers/getStateList`).subscribe({
      next: (res: any) => {
        this.stateList = Array.isArray(res?.data) ? res.data : [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching profile:', error);
        this.stateList = []; // fallback to empty to avoid .length error in UI
        this.isLoading = false;
      },
    });
  }

  onCheckboxChange(
    name: string,
    controlName: string,
    event: any,
    group?: FormGroup
  ): void {
    const targetGroup = group || this.enrolmentForm; // fallback to root form
    const formArray = targetGroup.get(controlName) as FormArray;

    if (!formArray) return;

    if (event.target.checked) {
      formArray.push(this.fb.control(name));
    } else {
      const index = formArray.controls.findIndex((x) => x.value === name);
      if (index >= 0) {
        formArray.removeAt(index);
      }
    }
  }

  get f() {
    return this.enrolmentForm.controls;
  }

  onStateChange(Event: any): void {
    const stateId = Event.target.value;
    if (stateId) {
      this.enrolmentForm.patchValue({ cityId: '', city: '' }); // reset city select
      this.cityList = []; // clear previous city list

      this.http.post('retailers/getCityList', { stateId }).subscribe({
        next: (res: any) => {
          if (res.code === 1 && res.data) {
            this.cityList = res.data;
          } else {
            this.cityList = [];
            this.notify.showSuccess(
              res.message || 'No cities found for this state',
              ''
            );
          }
        },
        error: (err) => {
          console.error('City fetch error:', err);
          this.notify.showError(
            'Failed to fetch cities. Please try again.',
            ''
          );
        },
      });
    }
  }

  onStateChanges(Event: any, index: number): void {
    const stateId = Event.target.value;
    if (!stateId) {
      this.cityLists[index] = [];
      this.additionalWorkshops.at(index).patchValue({ cityId: '' });
      return;
    }

    this.http.post('retailers/getCityList', { stateId }).subscribe({
      next: (res: any) => {
        if (res.code === 1 && res.data) {
          this.cityLists[index] = res.data;
        } else {
          this.cityLists[index] = [];
        }
        this.additionalWorkshops.at(index).patchValue({ cityId: '' });
      },
      error: () => {
        this.cityLists[index] = [];
      },
    });
  }

  sanitizeId(value: string): string {
    return 'business-' + value.replace(/\s+/g, '-').toLowerCase();
  }

  get additionalWorkshops(): FormArray {
    return this.enrolmentForm.get('additionalWorkshops') as FormArray;
  }

  getWorkshopGroup(index: number): FormGroup {
    return this.additionalWorkshops.at(index) as FormGroup;
  }

  addWorkshop() {
    const group = this.fb.group({
      additionalWorkshopId: [],
      branchName: ['', Validators.required],
      workshopAddress: ['', Validators.required],
      stateId: ['', Validators.required],
      state: [''],
      cityId: ['', Validators.required],
      city: [''],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      landmark: [''],
      locationCode: ['', Validators.required],
      equipments: this.fb.array([]),
      equipmentsOthers: [''],
      noOfMechanics: ['', Validators.required],
      managerName: ['', Validators.required],
      managerContact: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
      ],
      // mobileNo: ['', Validators.required],
      isAzeappUsernameNo: [false],
      azeappMblNo: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
      ],
      workingDaysOrHours: ['', Validators.required],
      weeklyHoliday: ['', Validators.required],
      isOwnedOrRented: [null],
      serviceBays: ['', Validators.required],
      customerWaitingArea: [],
      billingCounterWithPrinter: [],
      partsStockValue: ['', Validators.required],
    });

    this.additionalWorkshops.push(group);
  }

  onMobileSubmit(): void {
    if (this.mobileForm.invalid) return;

    this.isLoading = true;

    this.http
      .post('retailers/checkAndGetEnrollmentDatas', this.mobileForm.value)
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (res.code === 1) {
            switch (res.message) {
              case 'Waiting For Approval':
                this.mobileErrorMessage =
                  'Your application is pending approval. Please wait or contact support.';
                return;

              case 'Mobile Number Not Found':
                this.mobileErrorMessage =
                  'Mobile number not found. Please check and try again.';
                return;

              case 'Already Enrolled With This Mobile Number':
                this.mobileErrorMessage =
                  'This number is already enrolled. Please log in or contact support.';
                return;

              case 'Already Rejected':
                this.mobileErrorMessage =
                  'Your previous application was rejected. Please contact support before reapplying.';
                return;

              case 'Approved':
                this.mobileErrorMessage = '';
                this.showModal = false;
                this.patchEnrolmentForm(res.data); // 👈 Patch values here
                return;

              default:
                this.notify.showSuccess(res.message, '');
                this.router.navigate(['/login']);
            }
          } else {
            this.notify.showError(res.message, '');
          }
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Submission Error:', err);
          this.notify.showError('An error occurred. Please try again.', '');
        },
      });
  }

  onEnrolmentSubmit(): void {
    this.submitted = true;

    if (!this.agreedToTerms) {
      this.showTermsError = true;
      return;
    } else {
      this.showTermsError = false;
    }

    const workshopsArray = this.enrolmentForm.get(
      'additionalWorkshops'
    ) as FormArray;
    if (workshopsArray && workshopsArray.length > 0) {
      workshopsArray.controls.forEach((group: FormGroup) => {
        if (group.get('isAzeappUsernameNo')?.value) {
          group.patchValue({ azeappMblNo: group.get('managerContact')?.value });
        }
      });
    }

    if (this.enrolmentForm.invalid) {
      return;
    }

    const mobileNo = this.enrolmentForm.get('mobileNo')?.value;

    const payload = {
      mobileNo: mobileNo,
    };

    this.isLoading = true;

    this.http.post('retailers/sendOtpForEnrollment', payload).subscribe({
      next: (res: any) => {
        this.isLoading = false;

        if (res.code === 1) {
          this.notify.showSuccess('OTP sent successfully', '');
          this.showOtpModal = true;
        } else {
          this.notify.showError(res.message || 'Failed to send OTP', '');
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('OTP Generation Error:', err);
        this.notify.showError('Error sending OTP. Please try again.', '');
      },
    });
  }

  removeWorkshop(index: number): void {
    const workshops = this.enrolmentForm.get('additionalWorkshops');
    if (workshops && workshops instanceof FormArray) {
      workshops.removeAt(index);
    }
  }

  openTermsModal() {
    this.showTermsModal = true;
  }

  closeTermsModal() {
    this.showTermsModal = false;
  }

  patchEnrolmentForm(data: any): void {
    this.enrolmentForm.patchValue({
      membershipApplicationId: data.membershipApplicationId,
      workshopName: data.workshopName,
      branchName: data.branchName,
      tradeName: data.tradeName,
      workshopType: data.workshopType,
      gstNo: data.gstNo,
      panNo: data.panNo,
      workshopAddress: data.workshopAddress,
      pincode: data.pincode,
      locationCode: data.locationCode,
      stateId: data.stateId,
      state: data.state,
      cityId: data.cityId,
      city: data.city,
      ownerName: data.ownerName,
      mobileNo: data.mobileNo,
      alternateMobileNo: data.alternateMobileNo,
      managerName: data.managerName,
      managerContact: data.managerContact,
      noOfMechanics: data.noOfMechanics,
      workingDaysOrHours: data.workingDaysOrHours,
      weeklyHoliday: data.weeklyHoliday,
      isOwnedOrRented: data.isOwnedOrRented,
      serviceBays: data.serviceBays,
      customerWaitingArea: data.customerWaitingArea,
      billingCounterWithPrinter: data.billingCounterWithPrinter,
      isWms: data.isWms,
      wmsName: data.wmsName,
      azeappSolutionAdoption: data.azeappSolutionAdoption,
      bankName: data.bankName,
      accountHolderName: data.accountHolderName,
      accountNumber: data.accountNumber,
      ifscCode: data.ifscCode,
      upiId: data.upiId,
      minimumMonthlySource: data.minimumMonthlySource,
    });

    // ✅ Populate main city list from preloaded data
    if (data.stateId) {
      this.cityList = [];

      this.http
        .post('retailers/getCityList', { stateId: data.stateId })
        .subscribe({
          next: (res: any) => {
            if (res.code === 1 && res.data) {
              this.cityList = res.data;
            } else {
              this.cityList = [];
            }
          },
          error: (err) => {
            this.cityList = [];
            console.error('City fetch error:', err);
          },
        });
    }

    // Patch FormArrays
    this.setFormArray('equipments', data.equipments || []);
    this.setFormArray('language', data.language ? [data.language] : []);
    this.setFormArray('declarations', data.declarations || []);

    // Handle additional workshops
    if (data.additionalWorkshops?.length) {
      const awArray = this.enrolmentForm.get(
        'additionalWorkshops'
      ) as FormArray;
      awArray.clear();
      this.cityLists = []; // reset before filling

      data.additionalWorkshops.forEach((ws, index) => {
        awArray.push(
          this.fb.group({
            additionalWorkshopId: [ws.additionalWorkshopId || ''],
            branchName: [ws.branchName || '', Validators.required],
            workshopAddress: [ws.workshopAddress || '', Validators.required],
            stateId: [ws.stateId || null, Validators.required],
            state: [ws.state || null],
            cityId: [ws.cityId || null, Validators.required],
            city: [ws.city || null],
            pincode: [
              ws.pincode || '',
              [Validators.required, Validators.pattern(/^\d{6}$/)],
            ],
            locationCode: [ws.locationCode || '', Validators.required],
            managerName: [ws.managerName || '', Validators.required],
            managerContact: [
              ws.managerContact || '',
              [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
            ],
            mobileNo: [ws.mobileNo || ''],
            isAzeappUsernameNo: [ws.isAzeappUsernameNo || null],
            azeappMblNo: [
              ws.azeappMblNo || '',
              [Validators.required, Validators.pattern(/^[0-9]{10}$/)],
            ],
            noOfMechanics: [ws.noOfMechanics || 0, Validators.required],
            workingDaysOrHours: [
              ws.workingDaysOrHours || '',
              Validators.required,
            ],
            weeklyHoliday: [ws.weeklyHoliday || '', Validators.required],
            isOwnedOrRented: [ws.isOwnedOrRented || null],
            serviceBays: [ws.serviceBays || 0, Validators.required],
            equipments: this.fb.array(ws.equipments || []),
            equipmentsOthers: [ws.equipmentsOthers || ''],
            customerWaitingArea: [''],
            billingCounterWithPrinter: [''],
            landmark: [ws.landmark || ''],
            partsStockValue: [ws.partsStockValue || '', Validators.required],
          })
        );

        // ✅ Load city list for this additional workshop
        if (ws.stateId) {
          this.http
            .post('retailers/getCityList', { stateId: ws.stateId })
            .subscribe({
              next: (res: any) => {
                if (res.code === 1 && res.data) {
                  this.cityLists[index] = res.data;
                } else {
                  this.cityLists[index] = [];
                }
              },
              error: () => {
                this.cityLists[index] = [];
              },
            });
        } else {
          this.cityLists[index] = [];
        }
      });
    }
  }

  setFormArray(fieldName: string, values: string[]) {
    const formArray = this.enrolmentForm.get(fieldName) as FormArray;
    if (formArray) {
      formArray.clear();
      values.forEach((val) => formArray.push(this.fb.control(val)));
    }
  }

  verifyOtp(): void {
    this.otpError = '';
    const mobileNo = this.enrolmentForm.get('mobileNo')?.value;
    const enteredOtp = this.otpCode?.trim();

    if (!enteredOtp || enteredOtp.length !== 6) {
      this.notify.showError('Please enter a valid 6-digit OTP.', '');
      return;
    }

    const payload = {
      mobileNo: mobileNo,
      otp: enteredOtp,
    };

    this.isLoading = true;

    this.http.post('retailers/verifyOtpForEnrollment', payload).subscribe({
      next: (res: any) => {
        this.isLoading = false;

        if (res.code === 1) {
          this.notify.showSuccess('OTP Verified Successfully', '');
          this.showOtpModal = false;

          // Now submit the actual form after successful OTP
          this.submitFinalForm();
        } else {
          this.otpError = res.message || 'OTP Verification Failed';
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('OTP Verification Error:', err);
        this.otpError = 'An error occurred while verifying OTP.';
      },
    });
  }

  submitFinalForm(): void {
    const formValue = this.enrolmentForm.value;

    const selectedState = this.stateList.find(
      (state) => state.id == formValue.stateId
    );

    const selectedCity = this.cityList.find(
      (city) => city.id == formValue.cityId
    );


    const getCityName = (cityId: any, list: any[]) => {
      const foundCity = list.find((city) => city.id == cityId);
      return foundCity ? foundCity.city : null;
    };

    const additionalWorkshopsWithNames = (
      formValue.additionalWorkshops || []
    ).map((workshop, i) => {
      const wsState = this.stateList.find(
        (state) => state.id == workshop.stateId
      );
      const wsCity = getCityName(workshop.cityId, this.cityLists[i] || []);

      return {
        ...workshop,
        state: wsState?.state || null,
        city: wsCity || null,
      };
    });

    const payload = {
      ...formValue,
      state: selectedState?.state || null,
      city: selectedCity?.city || null,
      // language: singleLanguage,
      additionalWorkshops: additionalWorkshopsWithNames,
    };

    this.isLoading = true;
    this.http.post('retailers/saveEnrollmentForms', payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.showOtpModal = false;

        if (res.code === 1) {
          this.notify.showSuccess(res.message, '');
          this.router.navigate(['/login']);
        } else {
          this.notify.showError(res.message, '');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.notify.showError('An error occurred. Please try again.', '');
      },
    });
  }

  resendOtp(): void {
    const mobileNo = this.enrolmentForm.get('mobileNo')?.value;

    const payload = { mobileNo };

    this.http.post('retailers/sendOtpForEnrollment', payload).subscribe({
      next: (res: any) => {
        if (res.code === 1) {
          this.notify.showSuccess('OTP resent successfully!', '');
          this.startResendCooldown();
        } else {
          this.notify.showError(res.message || 'Failed to send OTP', '');
        }
      },
      error: (err) => {
        console.error('OTP Generation Error:', err);
        this.notify.showError('Error sending OTP. Please try again.', '');
      },
    });
  }

  startResendCooldown(): void {
    this.resendCooldown = 90; // 90 seconds timer
    this.otpTimer = setInterval(() => {
      if (this.resendCooldown > 0) {
        this.resendCooldown--;
      } else {
        clearInterval(this.otpTimer);
      }
    }, 1000);
  }

  closeOtpModal(): void {
    this.showOtpModal = false;
    this.otpCode = '';
    clearInterval(this.otpTimer);
    this.resendCooldown = 0;
  }

  onAgreeTerms(): void {
    this.agreedToTerms = true;
    this.showTermsError = false;
    this.closeTermsModal();
  }

  allowOnlyText(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    if (
      !(
        (charCode >= 65 && charCode <= 90) ||
        (charCode >= 97 && charCode <= 122) ||
        charCode === 32
      )
    ) {
      event.preventDefault();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
